import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL;

/**
 * Central data hook for the Package Dashboard.
 *
 * Manages three data domains independently so each view only
 * fetches what it needs:
 *   - stats:     KPI counts + stuck packages (overview)
 *   - packages:  paginated filtered list (package table)
 *   - inventory: stock summary (inventory view)
 *
 * Also exposes shared actions: bulk status update, search.
 */
export function usePackageDashboard() {

  // ── Stats (overview) ──────────────────────────────────────────────────────
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [statsError, setStatsError] = useState(null);

  // ── Package list ──────────────────────────────────────────────────────────
  const [packages, setPackages] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [packagesLoading, setPackagesLoading] = useState(false);
  const [packagesError, setPackagesError] = useState(null);

  // Active filters for the package table
  const [filters, setFilters] = useState({
    status: '',
    delivery_status: '',
    user_id: '',
    provider_id: '',
    district: '',
    date_from: '',
    date_to: '',
    page: 1,
    per_page: 30,
  });

  // ── Search ────────────────────────────────────────────────────────────────
  const [searchResults, setSearchResults] = useState([]);
  const [searchPagination, setSearchPagination] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // ── Inventory ─────────────────────────────────────────────────────────────
  const [inventory, setInventory] = useState([]);
  const [inventorySummary, setInventorySummary] = useState(null);
  const [inventoryLoading, setInventoryLoading] = useState(false);
  const [inventoryError, setInventoryError] = useState(null);

  // ── Users (for user filter) ───────────────────────────────────────────────
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [activeUsersOnly, setActiveUsersOnly] = useState(true);

  // ── Package history (single package drill-down) ───────────────────────────
  const [history, setHistory] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);

  // ── Bulk action state ─────────────────────────────────────────────────────
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);

  // ─────────────────────────────────────────────────────────────────────────
  // FETCH: Stats + stuck packages
  // ─────────────────────────────────────────────────────────────────────────
  const fetchStats = useCallback(async () => {
    setStatsLoading(true);
    setStatsError(null);
    try {
      const { data } = await axios.get(`${API_URL}/api/admin/packages/stats`);
      setStats(data);
    } catch (err) {
      console.error('Error fetching package stats:', err);
      setStatsError('No se pudo cargar el resumen de paquetes');
    } finally {
      setStatsLoading(false);
    }
  }, []);

  // ─────────────────────────────────────────────────────────────────────────
  // FETCH: Paginated package list (respects current filters)
  // ─────────────────────────────────────────────────────────────────────────
  const fetchPackages = useCallback(async (overrideFilters = null) => {
    setPackagesLoading(true);
    setPackagesError(null);

    const activeFilters = overrideFilters || filters;

    // Build query params — only include non-empty values
    const params = {};
    Object.entries(activeFilters).forEach(([key, value]) => {
      if (value !== '' && value !== null && value !== undefined) {
        params[key] = value;
      }
    });

    try {
      const { data } = await axios.get(`${API_URL}/api/admin/packages`, { params });
      setPackages(data.packages);
      setPagination(data.pagination);
    } catch (err) {
      console.error('Error fetching packages:', err);
      setPackagesError('No se pudo cargar la lista de paquetes');
    } finally {
      setPackagesLoading(false);
    }
  }, [filters]);

  // ─────────────────────────────────────────────────────────────────────────
  // FETCH: Cross-field search
  // ─────────────────────────────────────────────────────────────────────────
  const searchPackages = useCallback(async (query, page = 1) => {
    if (!query || query.trim().length < 2) {
      setSearchResults([]);
      setSearchPagination(null);
      return;
    }

    setSearchLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/admin/packages/search`, {
        params: { q: query.trim(), page },
      });
      setSearchResults(data.packages);
      setSearchPagination(data.pagination);
      setSearchQuery(query);
    } catch (err) {
      console.error('Error searching packages:', err);
    } finally {
      setSearchLoading(false);
    }
  }, []);

  // ─────────────────────────────────────────────────────────────────────────
  // FETCH: History for a single package
  // ─────────────────────────────────────────────────────────────────────────
  const fetchPackageHistory = useCallback(async (packageId) => {
    if (!packageId) return;
    setHistoryLoading(true);
    setHistory(null);
    try {
      const { data } = await axios.get(
        `${API_URL}/api/admin/packages/${packageId}/history`
      );
      setHistory(data);
    } catch (err) {
      console.error('Error fetching package history:', err);
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  // ─────────────────────────────────────────────────────────────────────────
  // FETCH: Inventory summary
  // ─────────────────────────────────────────────────────────────────────────
  const fetchInventory = useCallback(async (lowStockOnly = false) => {
    setInventoryLoading(true);
    setInventoryError(null);
    try {
      const { data } = await axios.get(`${API_URL}/api/admin/inventory/summary`, {
        params: { low_stock_only: lowStockOnly },
      });
      setInventory(data.products);
      setInventorySummary(data.summary);
    } catch (err) {
      console.error('Error fetching inventory:', err);
      setInventoryError('No se pudo cargar el inventario');
    } finally {
      setInventoryLoading(false);
    }
  }, []);

  // ─────────────────────────────────────────────────────────────────────────
  // ACTION: Bulk status update
  // ─────────────────────────────────────────────────────────────────────────
  const bulkUpdateStatus = useCallback(async (packageIds, newStatus, userId) => {
    setBulkLoading(true);
    setBulkResult(null);
    try {
      const { data } = await axios.patch(
        `${API_URL}/api/admin/packages/bulk-status`,
        { package_ids: packageIds, new_status: newStatus, user_id: userId }
      );
      setBulkResult(data);

      // Refresh the package list and stats after bulk update
      await Promise.all([fetchPackages(), fetchStats()]);

      return data;
    } catch (err) {
      console.error('Error bulk updating status:', err);
      throw err;
    } finally {
      setBulkLoading(false);
    }
  }, [fetchPackages, fetchStats]);

  // ─────────────────────────────────────────────────────────────────────────
  // FETCH: Users with packages
  // ─────────────────────────────────────────────────────────────────────────
  const fetchUsers = useCallback(async (activeOnly = true) => {
    setUsersLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/admin/users/with-packages`, {
        params: { active_only: activeOnly },
      });
      setUsers(data.items);
    } catch (err) {
      console.error('Error fetching users:', err);
      setUsers([]);
    } finally {
      setUsersLoading(false);
    }
  }, []);

  // Fetch users when activeUsersOnly toggle changes
  useEffect(() => {
    fetchUsers(activeUsersOnly);
  }, [activeUsersOnly, fetchUsers]);

  // ─────────────────────────────────────────────────────────────────────────
  // FILTER HELPERS
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * Update one or more filters and reset to page 1.
   * Usage: applyFilter({ status: 'in_fastmax' })
   */
  const applyFilter = useCallback((newFilters) => {
    setFilters(prev => ({ ...prev, ...newFilters, page: 1 }));
  }, []);

  /** Reset all filters to defaults */
  const clearFilters = useCallback(() => {
    setFilters({
      status: '',
      delivery_status: '',
      user_id: '',
      provider_id: '',
      district: '',
      date_from: '',
      date_to: '',
      page: 1,
      per_page: 30,
    });
  }, []);

  /** Navigate to a specific page */
  const goToPage = useCallback((page) => {
    setFilters(prev => ({ ...prev, page }));
  }, []);

  // Re-fetch packages whenever filters change
  useEffect(() => {
    fetchPackages();
  }, [filters, fetchPackages]);

  return {
    // Stats
    stats,
    statsLoading,
    statsError,
    fetchStats,

    // Package list
    packages,
    pagination,
    packagesLoading,
    packagesError,
    fetchPackages,

    // Filters
    filters,
    applyFilter,
    clearFilters,
    goToPage,

    // Search
    searchResults,
    searchPagination,
    searchLoading,
    searchQuery,
    searchPackages,
    setSearchQuery,

    // History
    history,
    historyLoading,
    fetchPackageHistory,

    // Inventory
    inventory,
    inventorySummary,
    inventoryLoading,
    inventoryError,
    fetchInventory,

    // Bulk actions
    bulkLoading,
    bulkResult,
    bulkUpdateStatus,

    // Users
    users,
    usersLoading,
    activeUsersOnly,
    setActiveUsersOnly,
    fetchUsers,
  };
}
